<template>
  <div class="mb-2 title" style="height:100%">
    <div v-if="!hideAdd && canAdd">
      <v-menu v-if="availableItems" offset-y>
        <template v-slot:activator="{ on }">
          <v-hover v-slot="{ hover }">
            <v-btn
              :class="{ 'create-button': true, hovered: hover }"
              :disabled="disabled"
              color="success"
              icon
              v-on="on"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </v-hover>
        </template>

        <v-list>
          <v-list-item
            v-for="(item, index) in availableItems"
            :key="index"
            :disabled="canAdd && _.get(item, disabledField, false)"
            @click="onAdd(item)"
          >
            <slot name="available-item" v-bind="{ item: availableItems[index], index }" />
          </v-list-item>
        </v-list>
      </v-menu>

      <v-hover v-if="!availableItems && canAdd" v-slot="{ hover }">
        <v-btn
          :class="{ 'create-button': true, hovered: hover, 'titleless': (_.isEmpty(title) && !$slots.title) }"
          :disabled="disabled"
          color="success"
          icon
          @click="onAdd()"
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </v-hover>
    </div>

    <v-card
      :class="color"
      style="height:100%"
      :style="{ background: transparent ? 'transparent !important' : undefined }"
      flat
    >
      <v-card-title
        class="subtitle-1 py-2"
      >
        <div v-if="!_.isEmpty(title)">
          <v-icon
            v-if="icon"
            class="mr-2"
          >
            {{ icon }}
          </v-icon>

          {{ title }}
        </div>

        <slot v-else name="title" />
      </v-card-title>
      <!-- <v-card-title v-else class="subtitle-1 py-2">

      </v-card-title> -->

      <v-container
        v-if="!hideData && data"
        fluid
        :class="color"
      >
        <v-list
          class="list py-0"
          :class="color"
          :style="{ background: transparent ? 'transparent !important' : undefined }"
          :max-height="maxHeight"
        >
          <SlickList
            v-model="data"
            :transition-duration="250"
            :use-drag-handle="useDragHandle"
            :lock-axis="lockAxis"
            append-to="#app"
            :class="color"
          >
            <SlickItem
              v-for="(item, index) in data"
              :key="index"
              :index="index"
              :disabled="!canMove()"
            >
              <v-card class="ma-2">
                <v-list-item
                  v-if="isItemVisible(index)"
                  :class="{ 'bottom-border': separator, 'px-0': true }"
                >
                  <v-hover v-if="_.size(data) > 1 && canMove(index)" v-slot="{ hover }">
                    <v-list-item-icon class="d-flex align-self-center ml-3">
                      <v-icon
                        v-handle
                        :class="{ 'move-button': true, hovered: hover }"
                      >
                        mdi-menu
                      </v-icon>
                    </v-list-item-icon>
                  </v-hover>

                  <div
                    v-else-if="canMove()"
                    class="ml-3"
                    style="width: 56px"
                  />

                  <slot name="list-item" v-bind="{ item: data[index], index }" />

                  <v-list-item-action
                    v-if="(!showEmpty || (showEmpty && _.size(data) > 1))
                      && hasRemoveRights && canRemove(index)"
                  >
                    <v-hover v-slot="{ hover }">
                      <v-btn
                        :class="{ 'remove-button': true, hovered: hover, 'mr-2': true }"
                        :disabled="disabled"
                        color="error"
                        icon
                        @click.prevent="onRemove(data[index])"
                      >
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-hover>
                  </v-list-item-action>

                  <v-list-item-action
                    v-else-if="hasRemoveRights && canRemove()"
                    style="width: 44px"
                  />
                </v-list-item>
              </v-card>
            </SlickItem>
          </SlickList>
        </v-list>
      </v-container>
    </v-card>


    <v-dialog
      v-model="removeDialog"
      max-width="300px"
      persistent
    >
      <v-card>
        <v-card-title class="headline">
          {{ $tc('dialogs.remove.title', 1) }}
        </v-card-title>
        <v-card-text>
          {{ $tc('dialogs.remove.message', 1) }}
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            color="error darken-1"
            text
            @click="removeDialog = false"
          >
            {{ $t('common.no') }}
          </v-btn>

          <v-btn
            color="success darken-1"
            text
            @click="removeConfirm"
          >
            {{ $t('common.yes') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { SlickList, SlickItem, HandleDirective } from 'vue-slicksort';
import { mapActions } from 'vuex';

export default {
  components: {
    SlickList,
    SlickItem,
  },

  directives: {
    handle: HandleDirective,
  },

  props: {
    // List data to sync
    value: { type: Array,
      default: () => [
      ] },

    hideData: Boolean,

    showEmpty: Boolean,

    // Optional list to display instead of value
    displayModel: Array,

    // Don't auto add, just emit a before-add event
    beforeAdd: Boolean,

    // Don't auto remove, just emit a before-remove event
    beforeRemove: Boolean,

    // Before removing an item, should we have a confirmation dialog?
    confirmRemove: Boolean,

    icon: String,

    color: String,

    // Show a separator line between each list item
    separator: Boolean,

    // Maximum height of the container region of the list
    maxHeight: Number,

    // Rights to check for create and remove availability
    rightsGroup: { type: String, default: 'address' },

    useDragHandle: Boolean,

    transparent: Boolean,

    lockAxis: { type: String, default: 'y' },

    disabled: Boolean,

    // Should we hide the add items/button
    hideAdd: Boolean,

    // Should we hide the remove button
    hideRemove: Boolean,

    // Can you add items to the list
    allowAdd: Boolean,

    // Should we hide the remove button
    allowRemove: [
      Boolean, Function
    ],

    // Can you reorder items in the list
    allowMove: [
      Boolean, Function
    ],

    // Title of the group card
    title: String,

    // Available items to choose from to add to list
    availableItems: Array,

    // Items disabled field
    disabledField: String,

    /**
     * When adding a new item to the list
     * if it's a String, it will add that field value from item selected
     * if it's a Function, it will use the value returned by a function
     */
    itemData: { type: [
      String, Function, Object
    ],
    default: () => {} },

    // Filter function
    filter: Function,
  },

  data() {
    return {
      data: [
      ],
      removeDialog: false,
      removeEntity: undefined,
    };
  },

  asyncComputed: {
    async canAdd() {
      const fn = _.isFunction(this.allowAdd)
        ? this.allowAdd
        : () => this.allowAdd;
      const allow = !this.disabled && !this.hideAdd;
      const hasRight = await this.hasRights(`${this.rightsGroup}.create`);
      const ok = _.isEmpty(this.rightsGroup) || hasRight;
      return ok && allow && fn();
    },

    async hasRemoveRights() {
      const hasRight = await this.hasRights(`${this.rightsGroup}.delete`);
      return _.isEmpty(this.rightsGroup) || hasRight;
    },
  },

  computed: {
    /**
     * Deep compares data to property value.
     */
    isDataModified() {
      return !_.isEqual(this.data, this.value);
    },

    isTitleSlotEmpty() {
      return !this.$slots.title;
    },
  },

  watch: {
    value: {
      deep: true,

      handler() {
        this.data = this.cloneValue();
      }
    },

    displayModel: {
      deep: true,

      handler() {
        this.data = this.cloneValue();
      }
    },

    data: {
      deep: true,

      handler(newValue) {
        if (this.isDataModified) {
          this.$emit('input', newValue);
        }
      }
    },
  },

  mounted() {
    this.data = this.cloneValue();

    // if (this.showEmpty && _.isEmpty(this.data)) {
    //   this.onAdd();
    // }
  },

  updated() {
    if (this.showEmpty && _.isEmpty(this.data)) {
      this.onAdd();
    }
  },

  activated() {
    if (this.showEmpty && _.isEmpty(this.data)) {
      this.onAdd();
    }
  },

  methods: {
    ...mapActions('auth', [
      'hasRights'
    ]),

    cloneValue() {
      return _.cloneDeep(this.displayModel || this.value);
    },

    newItemDataFrom(item) {
      if (_.isString(this.itemData)) {
        return _.get(item, this.itemData);
      }
      if (_.isFunction(this.itemData)) {
        return this.itemData.call(this, item);
      }
      return this.itemData || {};
    },

    onAdd(item) {
      if (!item && this.beforeAdd) {
        this.$emit('before-add');
      } else {
        const c = this.newItemDataFrom(item);
        this.data.push(c);
        this.$emit('add-list-item', c);
        return c;
      }
      return undefined;
    },

    onRemove(item) {
      if (this.beforeRemove) {
        this.$emit('before-remove', item);
      } else if (this.confirmRemove) {
        this.removeDialog = true;
        this.removeEntity = item;
      } else {
        return this.removeItem(item);
      }
      return -1;
    },

    removeConfirm() {
      this.removeItem(this.removeEntity);
      this.removeEntity = undefined;
    },

    removeItem(item) {
      this.removeDialog = false;
      const index = this.data.indexOf(item);
      this.data.splice(index, 1);
      this.$emit('remove-list-item', item, index);
      return index;
    },

    canMove(index) {
      const allow = !this.disabled;
      const fn = _.isFunction(this.allowMove)
        ? this.allowMove
        : () => this.allowMove;
      return allow && fn(index);
    },

    canRemove(index) {
      const allow = !this.disabled && !this.hideRemove;
      const fn = _.isFunction(this.allowRemove)
        ? this.allowRemove
        : () => this.allowRemove;
      return allow && fn(index);
    },

    isItemVisible(index) {
      return !_.isFunction(this.filter) || this.filter(index);
    },
  },
};
</script>

<style scoped>
.title {
  position: relative;
}
.white.bottom-border {
  border-bottom: 1px solid rgb(210, 210, 210) !important;
}
.create-button {
  position: absolute;
  right: 0;
  margin-right: .75em;
  margin-top: .25em;
  opacity: .5;
  z-index: 6;
}
.create-button.titleless {
  margin-top: -1.75em;
}
.remove-button {
  opacity: .5;
}
.move-button {
  cursor: grab;
  opacity: .5;
}
.hovered {
  opacity: 1;
}
.list {
  overflow-y: auto;
}
.item {
  padding: 0 !important;
}
</style>
